import { FC, useCallback, useEffect, useState } from 'react';
import { EventFlowBase } from './EventFlowBase';
import { useCreateEventContext } from '../../../contexts/organizer/create-event/CreateEventContext';
import { useEditable } from '../../../contexts/organizer/EditableStepsContext';
import { LottieLoader } from '../../LottieLoader';
import { GradientTypography } from '../../gradients/GradientTypography';
import { Stack } from '@mui/material';
import { useRouter } from '../../../hooks/useRouter';
import type { TournamentTemplate } from '../../../../functions/src/util/organizer/event-templates/TournamentTemplateGenerator';

export const CreateEvent: FC = () => {
  const { event, eventCreationSettings } = useCreateEventContext();
  const { document, setDocumentPath: setEditableDocumentPath } = useEditable();
  const [documentPath, setDocumentPath] = useState<string | undefined>();
  const router = useRouter();

  const copyEvent = useCallback(async () => {
    if (!event?.id || !event?.gameId) {
      return;
    }
    const { copyTournament } = await import(
      '../../../firebaseCloud/tournament/organizer/copyTournament'
    );

    const { data } = await copyTournament({
      tournamentId: event.id,
      fieldsToOverwrite: {
        gameId: event.gameId,
        date: Date.now(),
        endDate: Date.now(),
        checkInTime: Date.now(),
        readyTime: Date.now(),
        payouts: [],
      } as any,
    });
    return data.tournamentNew.id;
  }, [event?.gameId, event?.id]);

  const createEventFromTemplate = useCallback(
    async (template: TournamentTemplate) => {
      if (!event?.gameId) {
        return;
      }
      const { createTemplateTournament } = await import(
        '../../../firebaseCloud/tournament/organizer/createTemplateTournament'
      );
      const { data } = await createTemplateTournament({
        gameId: event.gameId,
        template,
      });
      return data.tournamentId;
    },
    [event?.gameId],
  );

  useEffect(() => {
    // TODO: make this generic for Events
    const handler = async () => {
      if (!event?.gameId) {
        return;
      }
      if (eventCreationSettings.method === 'Edit' && event?.id) {
        setDocumentPath(`Game/${event.gameId}/Tournament/${event.id}`);
        return;
      }
      const newEventId =
        eventCreationSettings.method === 'Copy'
          ? await copyEvent()
          : await createEventFromTemplate('new');
      if (newEventId) {
        setDocumentPath(`Game/${event.gameId}/Tournament/${newEventId}`);
      }
    };
    handler();
  }, [
    event?.gameId,
    event?.id,
    eventCreationSettings.method,
    copyEvent,
    createEventFromTemplate,
  ]);

  useEffect(() => {
    if (documentPath) {
      setEditableDocumentPath(documentPath);
    }
  }, [documentPath, setEditableDocumentPath]);

  useEffect(() => {
    if (document && documentPath && event?.gameId) {
      router.push(
        `/game/${event?.gameId}/events?event=${documentPath}&isEditing=true`,
      );
    }
  }, [document, documentPath, event?.gameId, router]);
  return (
    <EventFlowBase
      title={`Create ${eventCreationSettings.eventType}`}
      nextPageKey={`Event Frequency`}
      ContinueButton={<></>}
      disableNav
      Content={
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          gap={4}
        >
          <GradientTypography
            gradientColor="primary.horizontal"
            variant="h5"
            textTransform={'uppercase'}
            fontWeight={700}
          >
            Creating your event...
          </GradientTypography>
          <LottieLoader sx={{ width: 60, height: 60 }} />
        </Stack>
      }
    />
  );
};
